import { useEffect, useState, type ReactNode } from 'react';
import { HelpTooltip } from '../../../common/HelpTooltip';
import { useToast } from '../../../../hooks/useToast';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import styles from '../ScenarioGenerator.module.css';
import type { SceneItemProps } from '../SceneItem';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { useMaterialLookup } from './useMaterialLookup';
import { SceneLocationControl } from './SceneLocationControl';
import { SwipeClipEntry } from './SwipeClipEntry';
import { StillSceneCaptureEntry } from './StillSceneCaptureEntry';
import { useUrlCaptureStarter } from '../../../../features/urlCapture/useUrlCaptureStarter';
import { isMotionEngineScenario } from '../motionScenario';
import {
  DEFAULT_STILL_BACKGROUND_COLOR,
  extendBlockedByPrevious,
  extendBlockedReasonText,
  isMotionScene,
  isStillImageScene,
  normalizeStillBackgroundColor,
  resolveStillBackgroundColor,
  stillSceneVideoMaterialId,
} from '../../../../features/scenarios/productionMode';
import {
  resolveLinkPreviousMode,
  deriveSceneInputFamily,
  deriveSceneVideoInputModeForUi,
  findPreviousVideoLastFrame,
  isExtendVideoInputModeOptionDisabled,
  extendVideoInputModeOptionDisabledReason,
  buildVideoInputModePatchPayload,
} from '../utils';

/** シーンの入力方式3択 (video_input_mode) のセグメント脇に出す短縮ラベル
 *  (docs/features/scene-location-and-production-mode-plan.md §3.2/§3.8)。 */
const VIDEO_INPUT_MODE_LABELS: Record<'frame' | 'extend' | 'auto' | 'still' | 'motion', string> = {
  frame: 'フレームを動画化',
  extend: '前の動画から延長',
  auto: '画像からAIおまかせ',
  still: '静止画／スクショ動画',
  motion: 'モーショングラフィックス',
};

/** 3択アイコンのホバーツールチップ (frame のみ正式名称のまま・ラベルより説明的)。名前だけにする (補足は VIDEO_INPUT_MODE_HELP)。 */
const VIDEO_INPUT_MODE_TOOLTIPS: Record<'frame' | 'extend' | 'auto' | 'still' | 'motion', string> = {
  frame: '静止画フレームから動画化',
  extend: '前の動画から延長',
  auto: '画像からAIおまかせ',
  still: '静止画のまま／スクショ動画',
  motion: 'モーショングラフィックス',
};

/** 選んでいる入力方式の補足。セグメント下のラベルの右端にヘルプツールチップで出す (利用者指示 2026-10-01: ホバーのツールチップには出さない)。 */
const VIDEO_INPUT_MODE_HELP: Partial<Record<'frame' | 'extend' | 'auto' | 'still' | 'motion', string>> = {
  still: '動画 AI を使わず、静止画を余白を背景色で埋めて表示します。URLのスクリーンショットからスクロール動画を作る (無料) ことも、動画素材をドロップして余白付きでそのまま使うこともできます。',
  motion: '動画 AI を使わず、台本の内容を文字と図形のアニメーションにします。BGM の拍に合わせます。',
};

/** 「静止画をそのまま使う」の余白の色の候補 (白 / 黒 + 任意の色)。 */
const STILL_BACKGROUND_PRESETS: ReadonlyArray<{ color: string; label: string }> = [
  { color: '#FFFFFF', label: '白' },
  { color: '#000000', label: '黒' },
];

type SceneFrameCarouselProps = Pick<SceneItemProps, 'scenario' | 'scene' | 'sceneKey'> & {
  /** Seedance の顔制約でユーザー画像を first_frame に渡せないシーンか (SceneItem 側で計算)。 */
  blockFirstFrameInput: boolean;
  /** 「延長」(前の動画とつなげる) が実効 ON か (docs/features/
   *  link-previous-scene-continuity-default-plan.md §9 step-1 項目3。SceneItem 側で計算)。 */
  frameLinkActive: boolean;
};

/**
 * Step 1 のプレビュー画像カルーセル (前 / 現在 / 次)。ドロップゾーン・フレーム生成
 * ボタン・確定ボタンを含む「最初のフレーム」の本体。
 *
 * フレームモードの上部には「場面継続／場面転換」(continues_previous_shot の編集。
 * SceneDetailColumn の既存ドロップダウンと同じデザイン・同じ正本を同期) +
 * 場面継続時のみ「フレーム／延長」の切替 (scenes.link_previous_video の別ビュー) を置く
 * (§9 step-1 項目1)。
 */
export function SceneFrameCarousel({
  scenario,
  scene,
  sceneKey: key,
  blockFirstFrameInput,
  frameLinkActive,
}: SceneFrameCarouselProps) {
  const {
    videoSettings,
    isGlobalDragging,
    isDraggingVideo,
    dragOverSceneKey,
    setDragOverSceneKey,
    expandedConfirmButton,
    videoDropZoneRefs,
    heroImageUrl,
    asin,
    manualProductId,
    creditBadgeText,
    isCreditInsufficient,
    isStorageExceeded,
    handleDropMaterial,
    handleDropVideoMaterial,
    handleGenerateScenePreview,
    handleClearFirstFrame,
    handleConfirmFirstFrame,
    openFrameDropdown,
    openMaterialsPanel,
    setPreviewModalMaterial,
    setPreviewModalSceneId,
    getSceneVideoSettings,
    updateSceneVideoSettings,
    updateSceneMutation,
    lastResult,
    setLastResult,
    pendingContinuityScenes,
    setPendingContinuityScenes,
    handleGenerateSwipeClip,
  } = useSceneHandlers();
  const {
    scenePreviews, scenePreviewIndex, setScenePreviewIndex,
    scenePreviewMaterials, scenePreviewLoading,
    confirmedFirstFrames, sceneVideoMaterialId,
  } = useSceneStateContext();
  const { resolveMaterial } = useMaterialLookup();
  const toast = useToast();
  // 「静止画／スクショ動画」シーンのスクリーンショット入口 (StillSceneCaptureEntry)。キャプチャ完了でシーンに画像が入り
  // 空表示 (ドロップゾーン内の選択肢) が消えてもダイアログを閉じないよう、開閉はこのカルーセルで持つ
  const [stillCaptureOpen, setStillCaptureOpen] = useState(false);
  // 生成キューのある画面だけ (StillSceneCaptureEntry と同じ条件)
  const stillCaptureAvailable = !!useUrlCaptureStarter() && !!handleGenerateSwipeClip;
  const { data: videoModelsForLink = [] } = useGenerationModels('video');

  const images = scenePreviews[key] || [];
  const materials = scenePreviewMaterials[key] || [];
  const currentIndex = scenePreviewIndex[key] || 0;
  const totalImages = images.length;
  const currentMaterial = materials[currentIndex];
  const hasPreview = images.length > 0;
  // モーショングラフィックスのシーンはフレーム画像を作らないので、生成済みなら動画のサムネイルを出す
  const motionVideoId = isMotionScene(scene)
    ? (sceneVideoMaterialId[key] || (scene as { video_material_id?: string | null }).video_material_id || null)
    : null;

  // 場面継続／場面転換 + シーンの入力方式3択 (§9 step-1 項目1・P2 step 4)。シーン1は
  // 継続元が無いため場面継続ドロップダウンの対象外 (order>1 ガード。3択自体はシーン1でも
  // 表示する)。場面継続ドロップダウンは参照画像モードでもここ (サムネイル領域の上) に
  // 表示し、映像・ビジュアルセクションからは撤去済み (SceneDetailColumn)。
  // シーンの入力系統 (P2 step 3。videoGenerationMode(シナリオ単位) の代わりにシーン単位で
  // 判定する。docs/features/scene-location-and-production-mode-plan.md §3.1)。
  const sceneFamily = deriveSceneInputFamily(scenario, scene);
  // 全編モーション (§10.5) は場面継続 / 延長を使わないので切替を出さない (制作方式の 5 択は出す)
  const showContinuitySwitch = (sceneFamily === 'frame' || sceneFamily === 'reference')
    && scene.order > 1
    && !isMotionEngineScenario(scenario);
  // 3択セレクタ (video_input_mode) は frame/reference のいずれの家系でも共通で表示する
  // (P2 step 4。旧「フレーム／延長」アイコン2択はフレーム家系限定だったが、3択は
  // reference 家系の「前の動画とつなげる」トグル (SceneVideoBox) の一本化先も兼ねる)。
  // ブランドカットは SceneLocationControl と同じ理由で対象外。
  const show3ChoiceSelector = (sceneFamily === 'frame' || sceneFamily === 'reference')
    && !scene.brand_cut_position;
  const sceneSettingsForLink = getSceneVideoSettings(key);
  const effectiveVideoModelForLink = sceneSettingsForLink.videoModel || (videoSettings as any).videoModel || 'veo-3.1';
  const linkPrevModeForScene = resolveLinkPreviousMode(effectiveVideoModelForLink, videoModelsForLink);
  // 「延長」オプションの活性条件 (§3.2): 先行する本編シーンがあり、かつ選択中の動画
  // モデルがつなぎ方式に対応していること。continues_previous_shot は 3択の活性条件には
  // 含めない (§3.2: 「連続ショットだが今回はフレームから再生成する」の逆も成立しうるため、
  // 3択自体は脚本属性から独立させる。前提未充足時の生成時 preflight は後続 step)。
  const prevSceneInfoForExtend = findPreviousVideoLastFrame(
    scenario.id, scenario.scenes || [], scene.order, sceneVideoMaterialId,
  );
  // 直前の本編シーンがモーショングラフィックス / 静止画なら「延長」は成立しない (BE の導出も優先モードへ落とす。
  // brand-cut-motion-graphics-plan.md §10.4「場面継続」)。UI 版のシーンは order なので order_number に写して渡す
  const extendBlocked = extendBlockedByPrevious(
    (scenario.scenes || []).map((sc) => ({ ...sc, order_number: sc.order })), scene.order,
  );
  const extendOptionDisabled = isExtendVideoInputModeOptionDisabled(
    prevSceneInfoForExtend.hasPrecedingScene, linkPrevModeForScene, extendBlocked,
  );
  const extendOptionDisabledReason = extendVideoInputModeOptionDisabledReason(
    prevSceneInfoForExtend.hasPrecedingScene, extendBlocked,
  );

  // 表示値: 明示値があればそれ、無ければ導出結果 (deriveSceneVideoInputModeForUi) を
  // 「自動（〜）」のラベルに添える (§3.2: 導出中か明示値かが分かる軽い表現)。「自動」を
  // 選んだ場合に何になるかのプレビューなので、現在の明示値の有無に関わらず常に
  // video_input_mode=null を仮定して導出する (deriveSceneVideoInputModeForUi は優先順位1
  // で明示値を最優先するため、scene をそのまま渡すと明示値がある間は常にその値が
  // そのまま返ってしまい「自動なら何になるか」のプレビューにならない)。
  const rawExplicitVideoInputMode = scene.video_input_mode ?? null;
  const explicitVideoInputMode: 'frame' | 'extend' | 'auto' | 'still' | 'motion' | null =
    rawExplicitVideoInputMode === 'frame' || rawExplicitVideoInputMode === 'extend'
    || rawExplicitVideoInputMode === 'auto' || rawExplicitVideoInputMode === 'still'
    || rawExplicitVideoInputMode === 'motion'
      ? rawExplicitVideoInputMode
      : null;
  // 「静止画をそのまま使う」(明示値のみ)。動画は生成せず、ドロップした静止画 (動画素材を紐付ければその動画。
  // stillSceneVideoMaterialId) を出力の比率に contain で収め、余白を still_background_color で塗って
  // 動画エディタ / レンダラー / BE 合成が描く。
  const isStillScene = isStillImageScene(scene);
  const stillBackgroundColor = resolveStillBackgroundColor(scene);
  // 「静止画／スクショ動画」シーンに紐付いた動画 (スクロール動画・ドロップした動画素材)。あればサムネイル枠に
  // その動画のサムネイルを出す (シーンの画像は元画像として裏に残る)。「採用しない」なら出さない
  const stillVideoId = isStillScene && !(scene as { video_excluded?: boolean | null }).video_excluded
    ? (sceneVideoMaterialId[key] || stillSceneVideoMaterialId(scene))
    : null;
  const derivedVideoInputMode = deriveSceneVideoInputModeForUi(
    scenario, { ...scene, video_input_mode: null },
  );
  // 明示値が 'extend' のまま前のシーンがモーション / 静止画に変わった (または AI が場面継続を付けた) とき、
  // 実効値は優先モードに落ちる。何で生成されるかを添えて知らせる (延長アイコンは無効化される)
  const extendBlockedNotice = extendBlocked && (scene.video_input_mode === 'extend' || scene.continues_previous_shot)
    ? `${extendBlockedReasonText(extendBlocked)}。「${VIDEO_INPUT_MODE_LABELS[derivedVideoInputMode]}」で生成します`
    : null;
  // 3択ラベルに表示している実効入力方式 (明示 > 導出)。延長シーンの
  // 「動画を生成してください」バー表示の判定にも使う。
  const displayedVideoInputMode = explicitVideoInputMode ?? derivedVideoInputMode;
  // 自シーンの動画が生成済みか (延長バーは未生成の間だけ出す)
  const ownVideoGenerated = !!((scene as { video_material_id?: string | null }).video_material_id
    || sceneVideoMaterialId[key]);

  const handleContinuityChange = async (newValue: boolean) => {
    // PATCH 完了まで動画・フレーム生成ボタンを非活性にし、保存と生成を直列化する
    // (§9 step-1 項目7・FE補助)。
    setPendingContinuityScenes((prev) => new Set(prev).add(key));
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: { continues_previous_shot: newValue },
      });
      if (lastResult) {
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) =>
              sc.id === scene.id ? { ...sc, continues_previous_shot: newValue } : sc
            ),
          })),
        } : prev));
      }
    } catch {
      toast.error('更新に失敗しました');
    } finally {
      setPendingContinuityScenes((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // シーンの入力方式3択の変更 (P2 step 4・§3.3.1)。P2 以降の書き込みは video_input_mode
  // のみ (link_previous_video は新規に書かない)。P1 の場面セレクタ・場面継続と同じ
  // 保存+ローカル state 反映パターンで、pendingContinuityScenes を共有して生成ボタンとの
  // 直列化も揃える。
  const handleVideoInputModeChange = async (rawValue: string) => {
    const payload = buildVideoInputModePatchPayload(rawValue);
    setPendingContinuityScenes((prev) => new Set(prev).add(key));
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload,
      });
      if (lastResult) {
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) =>
              sc.id === scene.id ? { ...sc, ...payload } : sc
            ),
          })),
        } : prev));
      }
      // ローカルの sceneVideoSettings.linkPreviousVideo (旧トグルの読み出し時導出値/
      // 明示値) が残っていると getSceneVideoSettings の再導出を上書きし続けてしまう
      // (§3.3.1)。明示選択のたびにクリアし、常に新しい video_input_mode から
      // 再導出させる。
      updateSceneVideoSettings(key, { linkPreviousVideo: undefined });
    } catch {
      toast.error('更新に失敗しました');
    } finally {
      setPendingContinuityScenes((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  // 余白の色の変更 (video_input_mode='still' のときだけ表示)。3択と同じ保存 + ローカル state
  // 反映パターン。null = 既定の白へ戻す。
  const handleStillBackgroundColorChange = async (rawColor: string | null) => {
    const normalized = rawColor === null ? null : normalizeStillBackgroundColor(rawColor);
    if (rawColor !== null && normalized === null) return;
    if ((normalized ?? DEFAULT_STILL_BACKGROUND_COLOR) === stillBackgroundColor) return;
    try {
      await updateSceneMutation.mutateAsync({
        sceneId: scene.id,
        payload: { still_background_color: normalized },
      });
      if (lastResult) {
        setLastResult((prev) => (prev ? {
          ...prev,
          scenarios: prev.scenarios.map((s: any) => ({
            ...s,
            scenes: s.scenes.map((sc: any) =>
              sc.id === scene.id ? { ...sc, still_background_color: normalized } : sc
            ),
          })),
        } : prev));
      }
    } catch {
      toast.error('更新に失敗しました');
    }
  };

  return (
                                <>
                                  {/* 場面継続／場面転換 (上位・既存ドロップダウン) +
                                      場面 (場面固有化 P1・SceneLocationControl) の横並び行 +
                                      シーンの入力方式3択 (下位・badge select。P2 step 4)。
                                      previewContainer は 16:9 の flex センタリングのため
                                      内側に置くとカルーセルと横並びに潰れる。兄弟要素として
                                      カードの上に配置する。場面セレクタ・3択はシーン1
                                      (継続ドロップダウンなし) でも表示する。ブランドカットは
                                      SceneLocationControl 側が null を返す。 */}
                                  {(showContinuitySwitch || !scene.brand_cut_position) && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                                      {/* 行の幅を列幅に固定する (width 100% + minWidth 0)。これが無いと場面 select の
                                          固有幅 (最長の場面名) が行の最小幅になり、列の右端を超えて隣の列へはみ出す。
                                          select 側の縮小・省略は SceneLocationControl (.sceneLocationSelect)。 */}
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
                                        {showContinuitySwitch && (
                                          <div className={styles.sceneModeIconGroup} role="group" aria-label="場面転換／場面継続">
                                            <button
                                              type="button"
                                              className={`${styles.sceneModeIconBtn} ${!scene.continues_previous_shot ? styles.sceneModeIconBtnActive : ''}`}
                                              onClick={() => { if (scene.continues_previous_shot) void handleContinuityChange(false); }}
                                              disabled={pendingContinuityScenes.has(key)}
                                              aria-pressed={!scene.continues_previous_shot}
                                              data-tooltip="場面転換"
                                            >
                                              <img src="/scene_transition.svg" alt="場面転換" />
                                            </button>
                                            <button
                                              type="button"
                                              className={`${styles.sceneModeIconBtn} ${scene.continues_previous_shot ? styles.sceneModeIconBtnActive : ''}`}
                                              onClick={() => { if (!scene.continues_previous_shot) void handleContinuityChange(true); }}
                                              disabled={pendingContinuityScenes.has(key)}
                                              aria-pressed={!!scene.continues_previous_shot}
                                              data-tooltip="場面継続"
                                            >
                                              <img src="/scene_continue.svg" alt="場面継続" />
                                            </button>
                                          </div>
                                        )}
                                        <SceneLocationControl scenario={scenario} scene={scene} />
                                      </div>
                                      {/* シーンの入力方式3択 (静止画フレーム/延長/AIおまかせ + 自動)。
                                          場面継続の行からは改行して置く (ユーザー指定・旧アイコン2択と
                                          同じ位置)。 */}
                                      {show3ChoiceSelector && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                          <div className={styles.sceneModeIconGroup} role="group" aria-label="このシーンの動画入力方式">
                                            {/* AUTO = video_input_mode 未設定 (NULL)。導出結果のアイコンが
                                                控えめに点灯し、AUTO 自体もアクティブ表示になる。 */}
                                            <button
                                              type="button"
                                              className={`${styles.sceneModeIconBtn} ${styles.sceneModeAutoBtn} ${!explicitVideoInputMode ? styles.sceneModeIconBtnActive : ''}`}
                                              onClick={() => { if (explicitVideoInputMode) void handleVideoInputModeChange(''); }}
                                              disabled={updateSceneMutation.isPending || pendingContinuityScenes.has(key)}
                                              aria-pressed={!explicitVideoInputMode}
                                              data-tooltip={`自動（現在: ${VIDEO_INPUT_MODE_TOOLTIPS[derivedVideoInputMode]}）`}
                                            >
                                              AUTO
                                            </button>
                                            {(['frame', 'extend', 'auto', 'still', 'motion'] as const).map((m) => {
                                              const isExplicit = explicitVideoInputMode === m;
                                              const isDerived = !explicitVideoInputMode && derivedVideoInputMode === m;
                                              const isDisabled = (m === 'extend' && extendOptionDisabled)
                                                || updateSceneMutation.isPending
                                                || pendingContinuityScenes.has(key);
                                              const tooltip = (m === 'extend' && extendOptionDisabled)
                                                ? extendOptionDisabledReason
                                                : VIDEO_INPUT_MODE_TOOLTIPS[m];
                                              const iconSrc = m === 'frame'
                                                ? '/scene_still_frame.svg'
                                                : m === 'extend' ? '/scene_extend.svg'
                                                  : m === 'still' ? '/website_scroll.svg'
                                                    : m === 'motion' ? '/scene_motion.svg' : '/scene_auto.svg';
                                              return (
                                                <button
                                                  key={m}
                                                  type="button"
                                                  className={`${styles.sceneModeIconBtn} ${isExplicit ? styles.sceneModeIconBtnActive : ''} ${isDerived ? styles.sceneModeIconBtnAuto : ''}`}
                                                  onClick={() => { if (!isExplicit) void handleVideoInputModeChange(m); }}
                                                  disabled={isDisabled}
                                                  aria-pressed={isExplicit || isDerived}
                                                  data-tooltip={tooltip}
                                                >
                                                  <img src={iconSrc} alt={VIDEO_INPUT_MODE_TOOLTIPS[m]} />
                                                </button>
                                              );
                                            })}
                                          </div>
                                          <span className={styles.frameLinkModeLabel} style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                            {VIDEO_INPUT_MODE_LABELS[explicitVideoInputMode ?? derivedVideoInputMode]}
                                            {VIDEO_INPUT_MODE_HELP[explicitVideoInputMode ?? derivedVideoInputMode] && (
                                              <HelpTooltip ariaLabel={`「${VIDEO_INPUT_MODE_TOOLTIPS[explicitVideoInputMode ?? derivedVideoInputMode]}」のヘルプ`}>
                                                {VIDEO_INPUT_MODE_HELP[explicitVideoInputMode ?? derivedVideoInputMode]}
                                              </HelpTooltip>
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {/* 「静止画をそのまま使う」の余白の色。出力の比率と合わない静止画を
                                          contain で収めたときの上下 / 左右の余白をこの色で塗る (動画エディタ /
                                          レンダラーで描画。この画面ではプレビューしない)。 */}
                                      {extendBlockedNotice && (
                                        <span className={styles.frameLinkModeLabel} style={{ color: 'rgba(255, 170, 90, 0.85)' }} onClick={(e) => e.stopPropagation()}>
                                          {extendBlockedNotice}
                                        </span>
                                      )}
                                      {isStillScene && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                                          <span className={styles.frameLinkModeLabel} style={{ whiteSpace: 'nowrap' }}>余白の色</span>
                                          <div className={styles.sceneModeIconGroup} role="group" aria-label="余白の色">
                                            {STILL_BACKGROUND_PRESETS.map((preset) => (
                                              <button
                                                key={preset.color}
                                                type="button"
                                                className={`${styles.sceneModeIconBtn} ${stillBackgroundColor === preset.color ? styles.sceneModeIconBtnActive : ''}`}
                                                onClick={() => void handleStillBackgroundColorChange(preset.color)}
                                                disabled={updateSceneMutation.isPending}
                                                aria-pressed={stillBackgroundColor === preset.color}
                                                data-tooltip={preset.label}
                                              >
                                                <span
                                                  aria-hidden="true"
                                                  style={{
                                                    display: 'block', width: 14, height: 14, borderRadius: 3,
                                                    background: preset.color, border: '1px solid rgba(245, 241, 230, 0.35)',
                                                  }}
                                                />
                                              </button>
                                            ))}
                                            <label
                                              className={`${styles.sceneModeIconBtn} ${STILL_BACKGROUND_PRESETS.every((p) => p.color !== stillBackgroundColor) ? styles.sceneModeIconBtnActive : ''}`}
                                              data-tooltip="任意の色"
                                              style={{ position: 'relative' }}
                                            >
                                              <span
                                                aria-hidden="true"
                                                style={{
                                                  display: 'block', width: 14, height: 14, borderRadius: 3,
                                                  background: stillBackgroundColor, border: '1px solid rgba(245, 241, 230, 0.35)',
                                                  backgroundImage: STILL_BACKGROUND_PRESETS.every((p) => p.color !== stillBackgroundColor)
                                                    ? undefined
                                                    : 'conic-gradient(#f66, #fc6, #6f6, #6cf, #66f, #f6f, #f66)',
                                                }}
                                              />
                                              <input
                                                type="color"
                                                aria-label="余白の色 (任意)"
                                                value={stillBackgroundColor}
                                                onChange={(e) => void handleStillBackgroundColorChange(e.target.value)}
                                                disabled={updateSceneMutation.isPending}
                                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                                              />
                                            </label>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                <div className={styles.previewContainer}>
                                  {/* 前の画像（左側に30pxだけ見える） */}
                                  {totalImages > 1 && currentIndex > 0 && (
                                    <div
                                      className={styles.prevImagePreview}
                                      onClick={() => setScenePreviewIndex(prev => ({ ...prev, [key]: currentIndex - 1 }))}
                                    >
                                      <img src={images[currentIndex - 1]} alt="前の画像" />
                                    </div>
                                  )}

                                  {/* 現在の画像（中央） */}
                                  <div
                                    ref={(el) => {
                                      if (el) {
                                        videoDropZoneRefs.current.set(key, el);
                                      } else {
                                        videoDropZoneRefs.current.delete(key);
                                      }
                                    }}
                                    className={`${styles.scenePreviewWrapper} ${dragOverSceneKey === key ? styles.dragOver : ''} ${videoSettings.aspectRatio === '9:16' ? styles.portrait : ''}`}
                                    style={{
                                      aspectRatio: videoSettings.aspectRatio.replace(':', ' / '),
                                      // 静止画をそのまま使うシーン: 動画エディタ / レンダラーと同じく余白を背景色で塗る
                                      ...(isStillScene && hasPreview ? { background: stillBackgroundColor } : {}),
                                    }}
                                    onDragOver={(e) => {
                                      if (isDraggingVideo) return;
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'copy';
                                    }}
                                    onDragEnter={(e) => {
                                      if (isDraggingVideo) return;
                                      e.preventDefault();
                                      setDragOverSceneKey(key);
                                    }}
                                    onDragLeave={(e) => {
                                      if (isDraggingVideo) return;
                                      e.preventDefault();
                                      // 子要素への移動も考慮して、現在の要素を離れた場合のみクリア
                                      if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) {
                                        setDragOverSceneKey(null);
                                      }
                                    }}
                                    onDrop={(e) => {
                                      if (isDraggingVideo) return;
                                      e.preventDefault();
                                      setDragOverSceneKey(null);

                                      try {
                                        const dataString = e.dataTransfer.getData('application/json');
                                        if (!dataString) {
                                          toast.error('ドロップされたデータが無効です');
                                          return;
                                        }

                                        const materialData = JSON.parse(dataString);
                                        // 動画素材の場合はhandleDropVideoMaterialを呼ぶ
                                        if (materialData.contentType && materialData.contentType.startsWith('video/')) {
                                          handleDropVideoMaterial(scenario, scene, materialData);
                                          return;
                                        }

                                        // 画像素材の場合
                                        handleDropMaterial(scenario, scene, materialData);
                                      } catch (error) {
                                        console.error('ドロップ処理エラー:', error);
                                        toast.error('素材のドロップに失敗しました');
                                      }
                                    }}
                                  >
                                    {isGlobalDragging && !isDraggingVideo ? (
                                      <div className={styles.dropPlaceholder}>
                                        <span>ドロップ</span>
                                      </div>
                                    ) : stillVideoId ? (
                                      /* 「静止画／スクショ動画」で動画が紐付いている: 動画のサムネイル (クリックで再生)。
                                         × (シーンの画像を消す) は出さない (消えるのは裏の静止画で、表示中の動画は残り紛らわしいため。
                                         動画を外すのは動画カードの「採用しない」)。サムネイルが無い動画は静止画を出す */
                                      <MotionVideoThumb
                                        materialId={stillVideoId}
                                        title="このシーンで使う動画を再生"
                                        alt="このシーンで使う動画のサムネイル"
                                        fallback={hasPreview ? (
                                          <img src={images[currentIndex]} alt="シーンの静止画" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                        ) : undefined}
                                      />
                                    ) : hasPreview && !frameLinkActive ? (
                                      /* 延長実効 ON 中は、前シーンの動画完了で実写ラスト
                                         フレームが連鎖してプレビューが入っても (フレームは
                                         使用されないため) カルーセルへ切り替えず、下の
                                         延長案内プレースホルダを表示し続ける。連鎖データは
                                         残るので、フレームへ切り替えると流用フレームが出る */
                                      <div
                                        style={{ position: 'relative', width: '100%', height: '100%' }}
                                        onMouseEnter={(e) => {
                                          const closeBtn = e.currentTarget.querySelector(`.${styles.frameCloseButton}`) as HTMLElement;
                                          if (closeBtn) closeBtn.style.opacity = '1';
                                        }}
                                        onMouseLeave={(e) => {
                                          const closeBtn = e.currentTarget.querySelector(`.${styles.frameCloseButton}`) as HTMLElement;
                                          if (closeBtn) closeBtn.style.opacity = '0';
                                        }}
                                      >
                                        <img
                                          src={images[currentIndex]}
                                          alt={`シーン${scene.order}の冒頭イメージ ${currentIndex + 1}/${totalImages}`}
                                          draggable={!!currentMaterial?.id}
                                          onDragStart={(e) => {
                                            if (!currentMaterial?.id) {
                                              e.preventDefault();
                                              return;
                                            }
                                            const sourceUrl = currentMaterial.source_url || currentMaterial.sourceUrl || currentMaterial.temp_path || images[currentIndex];
                                            // 商品紐づき (asin / has_product_links) も載せる。選択素材パネルのブランド解決や
                                            // 「AIに送る情報」トグルの表示条件に使う。一覧 API 由来なら has_product_links が
                                            // あり、無い (不明) ときは受け側 (ensureProductLinkFlag) が 1 件取得で補う
                                            e.dataTransfer.setData('application/json', JSON.stringify({
                                              id: currentMaterial.id,
                                              type: currentMaterial.folder_type === 'cloud' ? 'cloud' : 'temporary',
                                              sourceUrl,
                                              thumbnailUrl: currentMaterial.thumbnail_url || currentMaterial.thumbnailUrl || images[currentIndex],
                                              contentType: (currentMaterial.content_type as string) || 'image/png',
                                              width: currentMaterial.width,
                                              height: currentMaterial.height,
                                              asin: currentMaterial.asin ?? null,
                                              hasProductLinks: typeof currentMaterial.has_product_links === 'boolean' ? currentMaterial.has_product_links : undefined,
                                            }));
                                            e.dataTransfer.effectAllowed = 'copy';
                                          }}
                                          onClick={async () => {
                                            // 参照画像モードでは動画素材のモーダルを表示
                                            const targetMaterialId = sceneFamily === 'reference' && !isStillScene && sceneVideoMaterialId[key]
                                              ? sceneVideoMaterialId[key]
                                              : currentMaterial?.id;
                                            if (targetMaterialId) {
                                              const fullMaterial = await resolveMaterial(targetMaterialId);
                                              if (fullMaterial) {
                                                setPreviewModalMaterial(fullMaterial);
                                                setPreviewModalSceneId(scene.id);
                                              }
                                            }
                                          }}
                                          style={{
                                            cursor: currentMaterial ? 'pointer' : 'default',
                                            // 静止画をそのまま使うシーン: トリミングせず出力の比率に contain で収める (書き出しと同じ見え方)
                                            ...(isStillScene ? { objectFit: 'contain' as const } : {}),
                                          }}
                                        />
                                        {/* 自動縮退の可視化 (§3.6): 'frame' を選んでいても faceSafe
                                            (顔あり×Seedance×写実) では実際はフレームを strip して
                                            参照 (mmref) 送信になる。内部の自動分岐機構は変更せず、
                                            絵コンテカードの「動画は実写で生成されます」等と同じ
                                            サムネイル下部オーバーレイで乖離を明示する。実際にモードが
                                            「フレームを動画化」のときだけ出す (延長選択時は紛らわしいため)。 */}
                                        {blockFirstFrameInput && displayedVideoInputMode === 'frame' && (
                                          <span className={styles.scenePreviewFaceSafeNotice}>
                                            Seedanceでは顔の映り込んだフレーム画像を利用できません。これは参考画像であり、動画はAIおまかせで生成されます
                                          </span>
                                        )}
                                        {/* 延長シーン (reference 家系はこのカルーセル分岐で連鎖ラスト
                                            フレームが表示される) で自シーンの動画が未生成の間は、
                                            静止画を生成済み動画と見誤らないよう中央バーを重ねる。 */}
                                        {displayedVideoInputMode === 'extend' && !ownVideoGenerated && (
                                          <div className={styles.extendGeneratePromptBar}>
                                            動画を生成してください
                                          </div>
                                        )}
                                        {/* 画像があればXボタンを表示（クリアしてプレースホルダに戻す） */}
                                        <button
                                          type="button"
                                          className={styles.frameCloseButton}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleClearFirstFrame(scene.id, key, currentIndex);
                                          }}
                                          title="最初のフレームをクリア"
                                        >
                                          ×
                                        </button>
                                        {sceneFamily !== 'reference' && totalImages > 1 && currentMaterial?.id && (
                                          confirmedFirstFrames[key] === currentMaterial.id ? (
                                            <div className={styles.confirmIconBadge}>
                                              <img src="/check_2_green.svg" alt="確定済" />
                                            </div>
                                          ) : (
                                            <button
                                              type="button"
                                              className={`${styles.confirmIconButton} ${expandedConfirmButton === key ? styles.confirmIconButtonExpanded : ''}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleConfirmFirstFrame(scene.id, key, currentMaterial.id);
                                              }}
                                              aria-label="この画像に差し替える"
                                            >
                                              <span className={styles.confirmButtonLabel}>この画像に差し替える</span>
                                              <img src="/check_2_gray.svg" alt="" />
                                            </button>
                                          )
                                        )}
                                      </div>
                                    ) : (
                                      <div className={styles.previewPlaceholder}>
                                        {isStillScene ? (
                                          stillCaptureAvailable ? (
                                            <div className={styles.stillChoice}>
                                              {/* 「静止画フレームを生成 / または / 素材を〜」と同じ区切り (placeholderDivider) */}
                                              <span className={`${styles.stillChoiceText} ${styles.desktopOnly}`} onClick={() => openMaterialsPanel('manage')}>素材パネルから静止画をドロップ</span>
                                              <span className={`${styles.stillChoiceText} ${styles.mobileOnly}`} onClick={() => openMaterialsPanel('manage')}>素材を選択</span>
                                              <div className={styles.placeholderDivider}><span>または</span></div>
                                              <button
                                                type="button"
                                                className="btn-modal-secondary-sm"
                                                onClick={(e) => { e.stopPropagation(); setStillCaptureOpen(true); }}
                                              >
                                                スクショから動画生成
                                              </button>
                                            </div>
                                          ) : (
                                            <span style={{ color: 'rgba(245, 241, 230, 0.4)', fontSize: '0.75rem', padding: '0 12px', textAlign: 'center' }}>
                                              素材パネルから静止画をドロップしてください
                                            </span>
                                          )
                                        ) : motionVideoId ? (
                                          <MotionVideoThumb materialId={motionVideoId} />
                                        ) : sceneFamily === 'reference' ? (
                                          <span style={{ color: 'rgba(245, 241, 230, 0.3)', fontSize: '0.8rem', textAlign: 'center' }}>動画を生成してください</span>
                                        ) : frameLinkActive ? (
                                          // 延長が実効 ON: フレームは使用されないため生成 UI を出さない
                                          // (§9 step-1 項目5)。前のシーンの動画が完成していればその
                                          // ラストフレームを文脈として表示し、自シーンの動画が未生成の
                                          // 間は中央バー「動画を生成してください」を重ねる (静止画を
                                          // 生成済み動画と見誤って生成を見落とすのを防ぐ。2026-08-31
                                          // ユーザー指定)。前の動画が未完成の間は従来の案内文のみ。
                                          (() => {
                                            const prevSceneWithVideo = (scenario.scenes || [])
                                              .filter((s) => !s.brand_cut_position
                                                && s.order < scene.order
                                                && !!((s as { video_material_id?: string | null }).video_material_id
                                                  || sceneVideoMaterialId[`${scenario.id}-scene-${s.order}`]))
                                              .sort((a, b) => b.order - a.order)[0] as { last_frame_url?: string | null } | undefined;
                                            const extendPreviewUrl = prevSceneWithVideo?.last_frame_url
                                              || (hasPreview ? images[currentIndex] : null);
                                            if (!extendPreviewUrl) {
                                              return (
                                                <span style={{ color: 'rgba(245, 241, 230, 0.4)', fontSize: '0.75rem', padding: '0 12px', textAlign: 'center' }}>
                                                  場面継続のため前の動画から延長します
                                                </span>
                                              );
                                            }
                                            return (
                                              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                                <img
                                                  src={extendPreviewUrl}
                                                  alt="前のシーンのラストフレーム (ここから延長されます)"
                                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                                {!ownVideoGenerated && (
                                                  <div className={styles.extendGeneratePromptBar}>
                                                    動画を生成してください
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          <>
                                            <div className={`${styles.frameGenerateGroup} ${scenePreviewLoading[key] ? 'ai-loading-active' : ''}`}>
                                              <button
                                                type="button"
                                                className={`${styles.frameGenerateButton} ${videoSettings.aspectRatio === '9:16' ? styles.frameGenerateButtonWrap : ''}`}
                                                onClick={() => handleGenerateScenePreview(scenario, scene)}
                                                disabled={
                                                  scenePreviewLoading[key]
                                                  || (!heroImageUrl && !asin && !manualProductId)
                                                  || isCreditInsufficient
                                                  || isStorageExceeded
                                                  || pendingContinuityScenes.has(key)
                                                }
                                                data-tooltip={`${creditBadgeText}ポイントを消費します`}
                                                data-tooltip-delay=""
                                              >
                                                {scenePreviewLoading[key] ? (
                                                  <>
                                                    生成中
                                                    <span className="ai-loading-dots">
                                                      <span>.</span>
                                                      <span>.</span>
                                                      <span>.</span>
                                                    </span>
                                                  </>
                                                ) : (
                                                  <>
                                                    {/* faceSafe シーンのフレームは API 非添付の構図参照用イラスト
                                                        なので「参考用」と明示する (通常時は静止画フレーム =
                                                        first_frame 入力)。 */}
                                                    <span>{blockFirstFrameInput ? '参考用フレームを生成' : '静止画フレームを生成'}</span>
                                                    <span className={styles.creditBadge}>{creditBadgeText}</span>
                                                  </>
                                                )}
                                              </button>
                                              <div className={styles.frameModelDropdown}>
                                                <button
                                                  type="button"
                                                  className={styles.frameModelDropdownTrigger}
                                                  onClick={(e) => openFrameDropdown(`first-generate-${key}`, e.currentTarget)}
                                                  disabled={scenePreviewLoading[key]}
                                                >
                                                  <span className={styles.frameModelArrow}>▼</span>
                                                </button>
                                              </div>
                                            </div>
                                            {blockFirstFrameInput ? (
                                              // faceSafe シーン: フレームはイラスト絵コンテとして生成する。
                                              // 実写真の顔を防げないため素材の D&D / 選択は出さない。
                                              <span style={{ color: 'rgba(245, 241, 230, 0.3)', fontSize: '0.7rem', textAlign: 'center', padding: '0 8px' }}>
                                                Seedance の顔画像制限を避けるため、<br />このフレームは構図参照用のイラストとして生成されます
                                              </span>
                                            ) : (
                                              <>
                                                <div className={styles.placeholderDivider}><span>または</span></div>
                                                <span className={styles.desktopOnly} style={{ cursor: 'pointer', color: 'rgba(245, 241, 230, 0.3)' }} onClick={() => openMaterialsPanel('manage')}>素材をドラッグ＆ドロップ</span>
                                                <span className={styles.mobileOnly} style={{ cursor: 'pointer', color: 'rgba(245, 241, 230, 0.3)' }} onClick={() => openMaterialsPanel('manage')}>素材を選択</span>
                                              </>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* 次の画像（右側に30pxだけ見える） */}
                                  {totalImages > 1 && currentIndex < totalImages - 1 && (
                                    <div
                                      className={styles.nextImagePreview}
                                      onClick={() => setScenePreviewIndex(prev => ({ ...prev, [key]: currentIndex + 1 }))}
                                    >
                                      <img src={images[currentIndex + 1]} alt="次の画像" />
                                    </div>
                                  )}
                                </div>
                                {/* スワイプ動画 (docs/features/swipe-motion-clip-plan.md §4.1): 静止画シーンだけ、サムネイルの直下 */}
                                {isStillScene && (
                                  <div className={styles.stillSceneActions}>
                                    <SwipeClipEntry scenario={scenario} scene={scene} sceneKey={key} />
                                    <StillSceneCaptureEntry
                                      scenario={scenario}
                                      scene={scene}
                                      open={stillCaptureOpen}
                                      setOpen={setStillCaptureOpen}
                                      hasImage={!!scene.image_material_id}
                                      aspectRatio={videoSettings.aspectRatio}
                                    />
                                  </div>
                                )}
                                </>
  );
}

/** 生成済みモーションの動画サムネイル (クリックで素材プレビューを開いて再生)。素材が未読み込みなら 1 件取得する。
 *  絵コンテのモーションカード (StoryboardMotionCard) も同じサムネイルを使う。 */
export function MotionVideoThumb({
  materialId,
  title = '生成したモーションを再生',
  alt = '生成したモーションのサムネイル',
  fallback,
}: {
  materialId: string;
  title?: string;
  alt?: string;
  /** 素材にサムネイルが無いときの表示 (既定は「生成済み」の文字) */
  fallback?: ReactNode;
}) {
  const { setPreviewModalMaterial } = useSceneHandlers();
  const { findLoadedMaterial, resolveMaterial } = useMaterialLookup();
  const loaded = findLoadedMaterial(materialId);
  const [fetched, setFetched] = useState<{ id: string; material: any } | null>(null);
  const material = loaded ?? (fetched?.id === materialId ? fetched.material : undefined);
  const needsFetch = !loaded && fetched?.id !== materialId;

  useEffect(() => {
    if (!needsFetch) return;
    let cancelled = false;
    void resolveMaterial(materialId)
      .then((m) => { if (!cancelled) setFetched({ id: materialId, material: m ?? null }); })
      .catch(() => { if (!cancelled) setFetched({ id: materialId, material: null }); });
    return () => { cancelled = true; };
    // resolveMaterial は毎レンダー作り直されるので依存に入れない (素材 id が変わったときだけ取り直す)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId, needsFetch]);

  const thumbUrl: string | null = material?.thumbnail_url || material?.thumbnailUrl || null;
  if (!thumbUrl) {
    if (material !== undefined && fallback) return <>{fallback}</>;
    return <span style={{ color: 'rgba(245, 241, 230, 0.4)', fontSize: '0.75rem', textAlign: 'center' }}>{material === undefined ? '読み込み中…' : '生成済み'}</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (material) setPreviewModalMaterial(material); }}
      title={title}
      style={{ width: '100%', height: '100%', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
    >
      <img src={thumbUrl} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </button>
  );
}
